import _ from 'lodash'
import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import meetingLinkModel, {
  Availability,
  CreateAvailabilityDto,
  CreateMeetingLinkDto,
  MeetingLink,
  UpdateAvailabilityDto,
  UpdateMeetingLinkDto,
} from '@/core/models/meeting-link.model'
import { SearchMeetingLinksQuery, SearchRequestOptions } from '@/core/types/request'

import { RootState } from '@/redux/rootReducer'

export const meetingLinkAdapter = createEntityAdapter<MeetingLink, MeetingLink['_id']>({ selectId: (meetingLink) => meetingLink._id })
export const availabilitiesAdapter = createEntityAdapter<Availability, Availability['_id']>({ selectId: (availability) => availability._id })

const meetingLinkSelector = meetingLinkAdapter.getSelectors((state: RootState) => state.meetingLink.meetingLinks)

export const selectMeetingLinks = meetingLinkSelector.selectAll
export const selectMeetingLinkIds = meetingLinkSelector.selectIds
export const selectMeetingLinkById = meetingLinkSelector.selectById
export const selectMeetingLinkByName = (state: RootState, meetingLinkName?: string) => {
  return _.find(selectMeetingLinks(state), (meetingLink) => meetingLink.name === meetingLinkName)
}
export const selectNewlyCreatedMeetingLink = (state: RootState) => {
  const newlyCreatedItemId = state.meetingLink.newlyCreatedItemId
  if (newlyCreatedItemId) {
    return selectMeetingLinkById(state, newlyCreatedItemId)
  }
  return undefined
}
export const selectMeetingLinkSearchParams = (state: RootState) => state.meetingLink.search.params
export const selectMeetingLinkSearchResult = (state: RootState) => state.meetingLink.search.result

export const selectMeetingLinkCreateRequestTracker = (state: RootState) => state.meetingLink.requestTracker.create
export const selectMeetingLinkListRequestTracker = (state: RootState) => state.meetingLink.requestTracker.list
export const selectMeetingLinkUpdateRequestTracker = (state: RootState) => state.meetingLink.requestTracker.update
export const selectMeetingLinkDeleteRequestTracker = (state: RootState) => state.meetingLink.requestTracker.delete

const initialState = _.merge(meetingLinkModel.getInitialState(), {
  requestTracker: {
    create: { error: null, loading: false },
    update: { error: null, loading: false },
    list: { error: null, loading: false },
    delete: { error: null, loading: false },
  },
})

const meetingLinkSlice = createSlice({
  name: meetingLinkModel.reducerName,
  initialState,
  reducers: {
    createMeetingLink(state, action: PayloadAction<CreateMeetingLinkDto>) {
      state.requestTracker.create.loading = true
      state.newlyCreatedItemId = undefined
    },
    createMeetingLinkSuccess(state, action: PayloadAction<MeetingLink>) {
      const meetingLink = action.payload
      meetingLinkAdapter.setOne(state.meetingLinks, meetingLink)
      state.newlyCreatedItemId = meetingLink._id
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createMeetingLinkFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    getMeetingLink(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getMeetingLinkSuccess(state, action: PayloadAction<MeetingLink>) {
      const meetingLink = action.payload
      meetingLinkAdapter.setOne(state.meetingLinks, meetingLink)
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getMeetingLinkFailure(state, action) {
      state.requestTracker.get.error = action.payload
      state.requestTracker.get.loading = false
    },
    getMeetingLinkByName(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getMeetingLinks(state, action: PayloadAction<SearchMeetingLinksQuery | undefined>) {
      state.requestTracker.list.loading = true
    },
    getMeetingLinksSuccess(state, action: PayloadAction<MeetingLink[]>) {
      meetingLinkAdapter.setAll(state.meetingLinks, action.payload)
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    getMeetingLinksFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },
    listMeetingLinks: {
      reducer: (state, action: PayloadAction<{ query: SearchMeetingLinksQuery; options?: SearchRequestOptions }>) => {
        state.search.params.query = action.payload.query
        state.search.params.options = action.payload.options
        state.requestTracker.list.loading = true
      },
      prepare: (query: SearchMeetingLinksQuery, options?: SearchRequestOptions) => ({
        payload: {
          query,
          options,
        },
      }),
    },
    listMeetingLinksSuccess(state, action: PayloadAction<{ items: MeetingLink[]; totalCount: number }>) {
      const { items, totalCount } = action.payload
      meetingLinkAdapter.setAll(state.meetingLinks, items)
      state.search.result.totalCount = totalCount
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    listMeetingLinksFailure(state, action) {
      meetingLinkSlice.caseReducers.getMeetingLinksFailure(state, action)
    },
    updateMeetingLink: {
      reducer: (state, action: PayloadAction<{ meetingLinkId: string; updateMeetingLinkDto: UpdateMeetingLinkDto }>) => {
        state.requestTracker.update.loading = true
      },
      prepare: (meetingLinkId: string, updateMeetingLinkDto: UpdateMeetingLinkDto) => ({
        payload: { meetingLinkId, updateMeetingLinkDto },
      }),
    },
    updateMeetingLinkSuccess(state, action: PayloadAction<MeetingLink>) {
      meetingLinkAdapter.setOne(state.meetingLinks, action.payload)
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateMeetingLinkFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    createAvailability(state, action: PayloadAction<{ meetingId: string; createAvailabilityDto: CreateAvailabilityDto }>) {
      state.requestTracker.create.loading = true
      state.newlyCreatedItemId = undefined
    },
    createAvailabilitySuccess(state, action: PayloadAction<MeetingLink>) {
      const meetingLink = action.payload
      meetingLinkAdapter.setOne(state.meetingLinks, meetingLink)
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createAvailabilityFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    updateAvailability(state, action: PayloadAction<{ meetingLinkId: string; availabilityId: string; updateAvailabilityDto: UpdateAvailabilityDto }>) {
      state.requestTracker.update.loading = true
      state.newlyCreatedItemId = undefined
    },
    updateAvailabilitySuccess(state, action: PayloadAction<MeetingLink>) {
      const meetingLink = action.payload
      meetingLinkAdapter.setOne(state.meetingLinks, meetingLink)
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateAvailabilityFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    deleteAvailability(state, action: PayloadAction<{ meetingId: string; availabilityId: string }>) {
      state.requestTracker.delete.loading = true
    },
    deleteAvailabilitySuccess(state, action: PayloadAction<MeetingLink>) {
      const meetingLink = action.payload
      meetingLinkAdapter.setOne(state.meetingLinks, meetingLink)
      state.requestTracker.delete.error = null
      state.requestTracker.delete.loading = false
    },
    deleteAvailabilityFailure(state, action) {
      state.requestTracker.delete.error = action.payload
      state.requestTracker.delete.loading = false
    },
  },
})

export const {
  createMeetingLink,
  createMeetingLinkSuccess,
  createMeetingLinkFailure,
  getMeetingLink,
  getMeetingLinkByName,
  getMeetingLinkSuccess,
  getMeetingLinkFailure,
  getMeetingLinks,
  getMeetingLinksSuccess,
  getMeetingLinksFailure,
  listMeetingLinks,
  listMeetingLinksSuccess,
  listMeetingLinksFailure,
  updateMeetingLink,
  updateMeetingLinkSuccess,
  updateMeetingLinkFailure,
  createAvailability,
  createAvailabilitySuccess,
  createAvailabilityFailure,
  updateAvailability,
  updateAvailabilitySuccess,
  updateAvailabilityFailure,
  deleteAvailability,
  deleteAvailabilitySuccess,
  deleteAvailabilityFailure,
} = meetingLinkSlice.actions

export default meetingLinkSlice.reducer
